// ======================================================================
// FILE:
// src/modules/finance/transactions/receipt_voucher/services/receiptVoucherService.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Receipt Voucher
//
// PURPOSE:
// Frontend API Client for Finance Receipt Voucher.
//
// RESPONSIBILITIES
// - Receipt Voucher Header CRUD
// - Receipt Voucher Workflow
// - No accounting logic
// - No React code
// - No UI logic
// - No Toast
// - No Navigation
// - No State Management
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import axiosClient from "../../../../../api/axiosClient";


// ======================================================================
// API BASE URL
// ======================================================================

const BASE_URL =
    "/api/finance/receipt-vouchers";


// ======================================================================
// LIST RECEIPT VOUCHERS
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * LIST RECEIPT VOUCHERS
 * ----------------------------------------------------------------------
 *
 * Backend:
 * GET /api/finance/receipt-vouchers
 *
 * Supported filters are passed directly to the backend.
 *
 * Example:
 *
 * {
 *     company_id: 1,
 *     status: "DRAFT",
 *     payer_type: "CUSTOMER",
 *     receipt_mode: "BANK_TRANSFER",
 *     search: "ABC",
 *     date_from: "2026-04-01",
 *     date_to: "2026-04-30"
 * }
 *
 */

async function listReceiptVouchers(
    filters = {}
) {

    const response =
        await axiosClient.get(
            BASE_URL,
            {
                params: filters,
            }
        );


    const data =
        response?.data?.data;


    /*
     * Keep the service flexible because the backend
     * may return either:
     *
     * 1. Direct array
     * 2. { rows: [] }
     * 3. { items: [] }
     * 4. { data: [] }
     */

    if (Array.isArray(data)) {

        return {
            rows: data,
            summary: {},
            pagination: {},
        };

    }


    return {

        rows:
            data?.rows ||
            data?.items ||
            data?.data ||
            [],

        summary:
            data?.summary ||
            {},

        pagination:
            data?.pagination ||
            {},

    };

}


// ======================================================================
// GET RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * GET RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * GET /api/finance/receipt-vouchers/{id}
 *
 */

async function getReceiptVoucher(
    receiptVoucherId
) {

    const response =
        await axiosClient.get(
            `${BASE_URL}/${receiptVoucherId}`
        );


    return response.data.data;

}


// ======================================================================
// CREATE RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * CREATE RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/receipt-vouchers
 *
 * The page is responsible for preparing the payload.
 * The service simply sends it to the backend.
 *
 */

async function createReceiptVoucher(
    payload
) {

    const response =
        await axiosClient.post(
            BASE_URL,
            payload
        );


    return response.data.data;

}


// ======================================================================
// UPDATE RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * UPDATE RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * PUT /api/finance/receipt-vouchers/{id}
 *
 * Only Draft Receipt Vouchers can be updated.
 * Backend remains authoritative for this rule.
 *
 */

async function updateReceiptVoucher(
    receiptVoucherId,
    payload
) {

    const response =
        await axiosClient.put(
            `${BASE_URL}/${receiptVoucherId}`,
            payload
        );


    return response.data.data;

}


// ======================================================================
// DELETE RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * DELETE RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * DELETE /api/finance/receipt-vouchers/{id}
 *
 * Backend performs soft delete.
 *
 */

async function deleteReceiptVoucher(
    receiptVoucherId
) {

    const response =
        await axiosClient.delete(
            `${BASE_URL}/${receiptVoucherId}`
        );


    return response.data;

}


// ======================================================================
// SUBMIT RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * SUBMIT RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/receipt-vouchers/{id}/submit
 *
 */

async function submitReceiptVoucher(
    receiptVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${receiptVoucherId}/submit`,
            {
                company_id: companyId,
            }
        );


    return response.data.data;

}


// ======================================================================
// APPROVE RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * APPROVE RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/receipt-vouchers/{id}/approve
 *
 */

async function approveReceiptVoucher(
    receiptVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${receiptVoucherId}/approve`,
            {
                company_id: companyId,
            }
        );


    return response.data.data;

}


// ======================================================================
// POST RECEIPT VOUCHER
// ======================================================================

/**
 * ----------------------------------------------------------------------
 * POST RECEIPT VOUCHER
 * ----------------------------------------------------------------------
 *
 * Backend:
 * POST /api/finance/receipt-vouchers/{id}/post
 *
 * IMPORTANT:
 * Actual accounting journal creation/posting remains
 * a backend Finance Runtime responsibility.
 *
 */

async function postReceiptVoucher(
    receiptVoucherId,
    companyId
) {

    const response =
        await axiosClient.post(
            `${BASE_URL}/${receiptVoucherId}/post`,
            {
                company_id: companyId,
            }
        );


    return response.data.data;

}


// ======================================================================
// EXPORT
// ======================================================================

const receiptVoucherService = {

    // --------------------------------------------------------------
    // Receipt Voucher Header
    // --------------------------------------------------------------

    listReceiptVouchers,

    getReceiptVoucher,

    createReceiptVoucher,

    updateReceiptVoucher,

    deleteReceiptVoucher,


    // --------------------------------------------------------------
    // Workflow
    // --------------------------------------------------------------

    submitReceiptVoucher,

    approveReceiptVoucher,

    postReceiptVoucher,

};


export default receiptVoucherService;